import { useState, useEffect, useRef } from "react"
import { useFirestoreUsersApproved } from "../../firebase/useFirestore"
import { db } from "../../firebase/config"
import { client } from "../../hooks/Client"
import permissionsCatalog from "../../helpers/permissionsCatalog"
import callTeamFunction from "../../helpers/team/callTeamFunction"
import rolePresets from "../../helpers/rolePresets"
import usePermissions from "../../hooks/usePermissions"
import roleWorksIn, { ROLE_AREAS, NO_AREAS } from "../../helpers/roleAreas"
import Modal from "../common/Modal"
import Table from "../common/table/Table.jsx"
import TableCell from "../common/table/TableCell.jsx"
import deleteIcon from "../../images/icons/delete-icon.png"

const groupedCatalog = permissionsCatalog.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = []
    acc[p.category].push(p)
    return acc
}, {})

const UserRoles = ({ selectedOption }) => {
    const members = useFirestoreUsersApproved()
    const { has, ready, userRoles } = usePermissions()

    const [permissionsModalOpen, setPermissionsModalOpen] = useState(false)
    const [activeRoleId, setActiveRoleId] = useState(null)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [roleToDelete, setRoleToDelete] = useState(null)
    const [newRoleModalOpen, setNewRoleModalOpen] = useState(false)

    const autoCreatedRef = useRef(false)

    useEffect(() => {
        if (selectedOption !== "9") return
        if (!Array.isArray(userRoles)) return
        if (userRoles.length > 0) return
        if (autoCreatedRef.current) return
        autoCreatedRef.current = true

        // The server finds-or-creates the Beheerder role (ensureBeheerderRole),
        // so two tabs that both see an empty list don't double-create it.
        callTeamFunction("manageUserRole", { client, action: "ensureBeheerder" })
    }, [selectedOption, userRoles])

    // `preset` is undefined for a blank role, or a rolePresets entry.
    // Rechten van een rol schrijft alleen de server (roles.manage). Naam,
    // beschrijving en kleur blijven hieronder rechtstreeks: die geven niemand iets.
    const addRole = (preset) => {
        callTeamFunction("manageUserRole", {
            client,
            action: "create",
            name: preset?.name || "",
            description: preset?.description || "",
            permissions: preset?.permissions || [],
        })
        setNewRoleModalOpen(false)
    }

    const savePermissions = (role, permissions) =>
        callTeamFunction("manageUserRole", { client, action: "setPermissions", roleDocId: role.docid, permissions })

    // Toggle every permission in a category on or off at once — the catalog
    // is large, so checking 30 boxes one by one is impractical.
    const toggleCategory = (role, perms) => {
        const current = Array.isArray(role.Permissions) ? role.Permissions : []
        const catKeys = perms.map(p => p.key)
        const allOn = catKeys.every(k => current.includes(k))
        const next = allOn
            ? current.filter(k => !catKeys.includes(k))
            : [...new Set([...current, ...catKeys])]
        savePermissions(role, next)
    }

    const roleNameHandler = (e) => {
        const docid = e.target.dataset.docid
        db.collection("UserRoles").doc(docid).update({ Name: e.target.value })
    }

    const roleDescriptionHandler = (e) => {
        const docid = e.target.dataset.docid
        db.collection("UserRoles").doc(docid).update({ Description: e.target.value })
    }

    const roleColorHandler = (e) => {
        const docid = e.target.dataset.docid
        db.collection("UserRoles").doc(docid).update({ Color: e.target.value })
    }

    const togglePermission = (role, permKey) => {
        const current = Array.isArray(role.Permissions) ? role.Permissions : []
        const next = current.includes(permKey)
            ? current.filter(k => k !== permKey)
            : [...current, permKey]
        savePermissions(role, next)
    }

    /**
     * Zet een onderdeel aan of uit voor deze rol.
     *
     * Een lege lijst betekent 'alles', dus het laatste vinkje uitzetten zou de
     * rol juist ALLES laten zien. Daarom slaan we dan `NO_AREAS` op — een
     * sleutel die geen onderdeel is, zodat de lijst niet leeg wordt.
     */
    const toggleArea = (role, areaKey) => {
        const current = Array.isArray(role.Areas) && role.Areas.length > 0
            ? role.Areas
            : ROLE_AREAS.map(a => a.key)

        const next = current.includes(areaKey)
            ? current.filter(k => k !== areaKey)
            : [...current, areaKey]

        callTeamFunction("manageUserRole", {
            client,
            action: "setAreas",
            roleDocId: role.docid,
            areas: next.length > 0 ? next : [NO_AREAS],
        })
    }

    const openPermissionsModal = (role) => {
        setActiveRoleId(role.docid)
        setPermissionsModalOpen(true)
    }

    const askDelete = (role) => {
        const affectedUsers = (members || []).filter(
            m => m.UserRoleIDs?.[client] === role.ID
        )
        setRoleToDelete({ ...role, affectedUsers })
        setDeleteModalOpen(true)
    }

    const confirmDelete = async () => {
        if (!roleToDelete) return
        // De server haalt de rol ook weg bij iedereen die hem had.
        await callTeamFunction("manageUserRole", { client, action: "delete", roleDocId: roleToDelete.docid })
        setDeleteModalOpen(false)
        setRoleToDelete(null)
    }

    if (selectedOption !== "9") return null

    // Bootstrap escape hatch: if no one in the tenant currently holds
    // `roles.manage`, leave the tab open so the first admin can claim
    // the system. As soon as someone has the permission, the gate kicks in.
    const someoneCanManageRoles = Array.isArray(members) && members.some(m => {
        const roleId = m.UserRoleIDs?.[client]
        if (!roleId) return false
        const role = (userRoles || []).find(r => r.ID === roleId)
        return Array.isArray(role?.Permissions) && role.Permissions.includes("roles.manage")
    })

    if (ready && someoneCanManageRoles && !has("roles.manage")) {
        return (
            <div className="settings-container">
                <div className="profile-inner-container">
                    <div className="userroles-no-access">
                        <h2>Rollen</h2>
                        <p>Je hebt geen rechten om rollen te beheren. Vraag een beheerder om toegang.</p>
                    </div>
                </div>
            </div>
        )
    }

    const activeRole = userRoles?.find(r => r.docid === activeRoleId)

    return (
        <div className="settings-container">
            <div className="profile-inner-container">
                <div className="table-container">
                    <h2>Rollen</h2>
                    <Table
                        columns={[
                            { key: "name", label: "ROL" },
                            { key: "description", label: "OMSCHRIJVING", searchable: false },
                            { key: "color", label: "KLEUR", searchable: false },
                            { key: "permissions", label: "RECHTEN", searchable: false },
                            { key: "delete", label: "VERWIJDEREN", searchable: false },
                        ]}
                        items={userRoles || []}
                        getItemKey={(role) => role.docid}
                        renderRow={(role) => (
                            <>
                                <TableCell>
                                    <input
                                        type="text"
                                        defaultValue={role.Name}
                                        placeholder="Naam van de rol"
                                        data-docid={role.docid}
                                        onBlur={roleNameHandler}
                                    />
                                </TableCell>
                                <TableCell>
                                    <textarea
                                        defaultValue={role.Description}
                                        placeholder="Omschrijf hier de rol"
                                        data-docid={role.docid}
                                        onBlur={roleDescriptionHandler}
                                    />
                                </TableCell>
                                <TableCell>
                                    <input
                                        type="color"
                                        className="setting-color-picker"
                                        defaultValue={role.Color || "#47acc3"}
                                        data-docid={role.docid}
                                        onChange={roleColorHandler}
                                    />
                                </TableCell>
                                <TableCell>
                                    <button
                                        className="userroles-permissions-button"
                                        onClick={() => openPermissionsModal(role)}
                                    >
                                        {(role.Permissions?.length || 0)} / {permissionsCatalog.length} rechten
                                    </button>
                                </TableCell>
                                <TableCell>
                                    <img
                                        src={deleteIcon}
                                        alt=""
                                        className="userrole-users-delete-button"
                                        onClick={() => askDelete(role)}
                                    />
                                </TableCell>
                            </>
                        )}
                        searchAccessor={(role) => role?.Name || ""}
                        searchPlaceholder="Zoeken op rol"
                        emptyMessage="Nog geen rollen toegevoegd."
                        addRow={{
                            toolTipContent: "Rol toevoegen",
                            onClick: () => setNewRoleModalOpen(true),
                        }}
                    />
                </div>
            </div>

            <Modal
                modalOpen={permissionsModalOpen}
                setModalOpen={setPermissionsModalOpen}
                pageHeader={`Rechten — ${activeRole?.Name || "rol"}`}
                confirm={() => setPermissionsModalOpen(false)}
                confirmLabel="Klaar"
                content={
                    activeRole && (
                        <>
                        {/* Wat deze rol te zien krijgt. Staat boven de rechten
                            omdat het de grovere vraag is: eerst waar werk je in,
                            dan wat mag je daar. Verbergen is een gordijn — de
                            rechten hieronder zijn het slot.
                            AI-CONTEXT: See docs/features/rol-onderdelen.md */}
                        <div className="userroles-areas">
                            <h3>Onderdelen waarin deze rol werkt</h3>
                            <p className="userroles-areas-note">
                                Wat hier uit staat, verdwijnt uit het menu en uit de tabbladen.
                                Het blijft bereikbaar via een directe link; wat iemand écht mag,
                                regel je met de rechten hieronder.
                            </p>
                            {ROLE_AREAS.map(area => (
                                <label key={area.key} className="userroles-permission-row">
                                    <input
                                        type="checkbox"
                                        checked={roleWorksIn(activeRole, area.key)}
                                        onChange={() => toggleArea(activeRole, area.key)}
                                    />
                                    <div className="userroles-permission-text">
                                        <strong>{area.label}</strong>
                                        <span>{area.description}</span>
                                    </div>
                                </label>
                            ))}
                        </div>

                        <div className="userroles-permissions-grid">
                            {Object.entries(groupedCatalog).map(([category, perms]) => (
                                <div key={category} className="userroles-permissions-category">
                                    <div className="userroles-category-header">
                                        <h3>{category}</h3>
                                        <button
                                            type="button"
                                            className="userroles-category-toggle"
                                            onClick={() => toggleCategory(activeRole, perms)}
                                        >
                                            {perms.every(p => activeRole.Permissions?.includes(p.key)) ? "Alles uit" : "Alles aan"}
                                        </button>
                                    </div>
                                    {perms.map(p => {
                                        const checked = activeRole.Permissions?.includes(p.key) || false
                                        return (
                                            <label key={p.key} className="userroles-permission-row">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => togglePermission(activeRole, p.key)}
                                                />
                                                <div className="userroles-permission-text">
                                                    <strong>{p.label}</strong>
                                                    <span>{p.description}</span>
                                                </div>
                                            </label>
                                        )
                                    })}
                                </div>
                            ))}
                        </div>
                        </>
                    )
                }
            />

            <Modal
                modalOpen={deleteModalOpen}
                setModalOpen={setDeleteModalOpen}
                pageHeader="Rol verwijderen"
                confirm={confirmDelete}
                confirmLabel="Verwijderen"
                content={
                    roleToDelete && (
                        <div className="userroles-delete-warning">
                            <p>
                                Weet je zeker dat je <i>{roleToDelete.Name || "deze rol"}</i> wilt verwijderen?
                            </p>
                            {roleToDelete.affectedUsers.length > 0 && (
                                <div className="userroles-delete-affected">
                                    <h3>Let op!</h3>
                                    <p>
                                        {roleToDelete.affectedUsers.length === 1
                                            ? "1 teamlid heeft deze rol"
                                            : `${roleToDelete.affectedUsers.length} teamleden hebben deze rol`}
                                        {" "}— hun rechten worden gewist. Wijs ze daarna een andere rol toe.
                                    </p>
                                </div>
                            )}
                        </div>
                    )
                }
            />

            <Modal
                modalOpen={newRoleModalOpen}
                setModalOpen={setNewRoleModalOpen}
                pageHeader="Nieuwe rol"
                confirm={() => setNewRoleModalOpen(false)}
                confirmLabel="Annuleren"
                content={
                    <div className="userroles-preset-list">
                        {rolePresets.map(preset => (
                            <div
                                key={preset.id}
                                className="userroles-preset-option"
                                onClick={() => addRole(preset)}
                            >
                                <strong>{preset.name}</strong>
                                <span>{preset.description}</span>
                            </div>
                        ))}
                        <div
                            className="userroles-preset-option"
                            onClick={() => addRole()}
                        >
                            <strong>Lege rol</strong>
                            <span>Begin met een rol zonder rechten en stel zelf alles in.</span>
                        </div>
                    </div>
                }
            />
        </div>
    )
}

export default UserRoles
